import React, { useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useLanguage } from '../i18n/LanguageContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  overflow-y: auto;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  padding: 24px 30px;
  border-bottom: 2px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #0c4da2, #4f94ff);
  color: white;
  border-radius: 16px 16px 0 0;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  margin: 0;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 24px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const ModalBody = styled.div`
  padding: 30px;
`;

const Section = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  color: #0c4da2;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #0c4da2;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoLabel = styled.span`
  font-size: 13px;
  color: #718096;
  font-weight: 600;
`;

const InfoValue = styled.span`
  font-size: 15px;
  color: #2d3748;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #4a5568;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #0c4da2;
  }

  &:disabled {
    background: #f7fafc;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #0c4da2;
  }
`;

const PriceBox = styled.div`
  background: linear-gradient(135deg, #0c4da2, #4f94ff);
  color: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  margin: 20px 0;
`;

const PriceLabel = styled.div`
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 8px;
`;

const PriceValue = styled.div`
  font-size: 32px;
  font-weight: 700;
`;

const PriceBreakdown = styled.div`
  background: #f7fafc;
  padding: 16px;
  border-radius: 8px;
  margin-top: 12px;
  font-size: 13px;
  text-align: left;
  color: #2d3748;
`;

const BreakdownRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #e2e8f0;

  &:last-child {
    border-bottom: none;
    font-weight: 700;
    padding-top: 10px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 20px;
  border-top: 2px solid #e2e8f0;
`;

const Button = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.$variant === 'primary' && `
    background: linear-gradient(135deg, #38a169, #48bb78);
    color: white;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 16px rgba(56, 161, 105, 0.3);
    }
  `}

  ${props => props.$variant === 'danger' && `
    background: linear-gradient(135deg, #e53e3e, #fc8181);
    color: white;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 16px rgba(229, 62, 62, 0.3);
    }
  `}

  ${props => props.$variant === 'secondary' && `
    background: #e2e8f0;
    color: #4a5568;

    &:hover:not(:disabled) {
      background: #cbd5e0;
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;

  ${props => props.$status === 'pending' && `
    background: #fef3c7;
    color: #92400e;
  `}

  ${props => props.$status === 'validated' && `
    background: #d1fae5;
    color: #065f46;
  `}

  ${props => props.$status === 'rejected' && `
    background: #fee2e2;
    color: #991b1b;
  `}
`;

const Alert = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 14px;

  ${props => props.$type === 'success' && `
    background: #d1fae5;
    color: #065f46;
    border-left: 4px solid #059669;
  `}

  ${props => props.$type === 'error' && `
    background: #fee2e2;
    color: #991b1b;
    border-left: 4px solid #dc2626;
  `}
`;

const QuoteDetailModal = ({ quote, onClose, onUpdate }) => {
  const { t, locale } = useLanguage();
  const [editedQuote, setEditedQuote] = useState({
    transport_type: quote.transport_type || '',
    rate_per_km: quote.rate_per_km || 1.50,
    rate_per_tonne: quote.rate_per_tonne || 50.00,
    fixed_fees: quote.fixed_fees || 100.00,
    discount: quote.discount || 0,
    vat: quote.vat || 20.00,
    distance_km: quote.distance_km || 0,
    quantity: quote.quantity || 0
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Calculate price breakdown
  const calculatePrices = () => {
    const distance = parseFloat(editedQuote.distance_km) || 0;
    const quantity = parseFloat(editedQuote.quantity) || 0;
    const isRoundTrip = quote.is_round_trip;

    const effectiveDistance = isRoundTrip ? distance * 2 : distance;
    const distanceCost = effectiveDistance * parseFloat(editedQuote.rate_per_km);
    const quantityCost = quantity * parseFloat(editedQuote.rate_per_tonne);
    const fixedFees = parseFloat(editedQuote.fixed_fees);

    const subtotal = distanceCost + quantityCost + fixedFees;
    const discountAmount = (subtotal * parseFloat(editedQuote.discount)) / 100;
    const priceAfterDiscount = subtotal - discountAmount;
    const vatAmount = (priceAfterDiscount * parseFloat(editedQuote.vat)) / 100;
    const finalPrice = priceAfterDiscount + vatAmount;

    return {
      distanceCost: distanceCost.toFixed(2),
      quantityCost: quantityCost.toFixed(2),
      fixedFees: fixedFees.toFixed(2),
      subtotal: subtotal.toFixed(2),
      discountAmount: discountAmount.toFixed(2),
      priceAfterDiscount: priceAfterDiscount.toFixed(2),
      vatAmount: vatAmount.toFixed(2),
      finalPrice: finalPrice.toFixed(2)
    };
  };

  const prices = calculatePrices();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedQuote(prev => ({ ...prev, [name]: value }));
  };

  const handleRecalculate = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/quotes/calculate-price`, {
        distance_km: editedQuote.distance_km,
        quantity: editedQuote.quantity,
        is_round_trip: quote.is_round_trip,
        transport_type: editedQuote.transport_type,
        origin_country: quote.origin_country,
        destination_country: quote.destination_country,
        origin_continent: quote.origin_continent,
        destination_continent: quote.destination_continent
      });

      if (response.data.success) {
        const breakdown = response.data.data.breakdown;
        setEditedQuote(prev => ({
          ...prev,
          rate_per_km: breakdown.rate_per_km,
          rate_per_tonne: breakdown.rate_per_tonne,
          fixed_fees: breakdown.fixed_fees
        }));
        setSuccess(t('modal.quoteDetail.success.ratesUpdated'));
      }
    } catch (err) {
      console.error('Recalculate error:', err);
      setError(t('modal.quoteDetail.errors.rateFetch'));
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = async (e) => {
    const newType = e.target.value;
    setEditedQuote(prev => ({ ...prev, transport_type: newType }));
    
    if (!newType) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/quotes/calculate-price`, {
        distance_km: editedQuote.distance_km,
        quantity: editedQuote.quantity,
        is_round_trip: quote.is_round_trip,
        transport_type: newType,
        origin_country: quote.origin_country,
        destination_country: quote.destination_country,
        origin_continent: quote.origin_continent,
        destination_continent: quote.destination_continent
      });

      if (response.data.success) {
        const breakdown = response.data.data.breakdown;
        setEditedQuote(prev => ({
          ...prev,
          transport_type: newType,
          rate_per_km: breakdown.rate_per_km,
          rate_per_tonne: breakdown.rate_per_tonne,
          fixed_fees: breakdown.fixed_fees
        }));
      }
    } catch (err) {
      console.error('Error fetching rates for new type:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.patch(
        `${API_BASE_URL}/quotes/${quote.id}`,
        {
          ...editedQuote,
          final_price: parseFloat(prices.finalPrice)
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess('Pricing updated successfully!');
      setTimeout(() => {
        onUpdate(response.data.data);
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.message || t('modal.quoteDetail.errors.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    if (!window.confirm(t('modal.quoteDetail.confirm.validate'))) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('adminToken');
      
      // First update pricing
      await axios.patch(
        `${API_BASE_URL}/quotes/${quote.id}`,
        {
          ...editedQuote,
          final_price: parseFloat(prices.finalPrice)
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Then validate
      const response = await axios.post(
        `${API_BASE_URL}/quotes/${quote.id}/validate`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess(t('modal.quoteDetail.success.validated'));
      setTimeout(() => {
        onUpdate(response.data.data);
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || t('modal.quoteDetail.errors.validateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!window.confirm(t('modal.quoteDetail.confirm.reject'))) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API_BASE_URL}/quotes/${quote.id}/reject`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess(t('modal.quoteDetail.success.rejected'));
      setTimeout(() => {
        onUpdate(response.data.data);
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.message || t('modal.quoteDetail.errors.rejectFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{t('modal.quoteDetail.title', { id: String(quote.id).padStart(6, '0') })}</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          {success && <Alert $type="success">{success}</Alert>}
          {error && <Alert $type="error">{error}</Alert>}

          <Section>
            <SectionTitle>📋 {t('modal.quoteDetail.clientInfo')}</SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>{t('modal.quoteDetail.fields.name')}</InfoLabel>
                <InfoValue>{quote.name}</InfoValue>
              </InfoItem>
              {quote.company && (
                <InfoItem>
                  <InfoLabel>{t('modal.quoteDetail.fields.company')}</InfoLabel>
                  <InfoValue>{quote.company}</InfoValue>
                </InfoItem>
              )}
              <InfoItem>
                <InfoLabel>{t('modal.quoteDetail.fields.email')}</InfoLabel>
                <InfoValue>{quote.email}</InfoValue>
              </InfoItem>
              {quote.phone && (
                <InfoItem>
                  <InfoLabel>{t('modal.quoteDetail.fields.phone')}</InfoLabel>
                  <InfoValue>{quote.phone}</InfoValue>
                </InfoItem>
              )}
              <InfoItem>
                <InfoLabel>{t('modal.quoteDetail.fields.status')}</InfoLabel>
                <InfoValue>
                  <StatusBadge $status={quote.status}>
                    {t(`modal.quoteDetail.statusLabels.${quote.status}`) || quote.status}
                  </StatusBadge>
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>{t('modal.quoteDetail.fields.requestDate')}</InfoLabel>
                <InfoValue>{new Date(quote.created_at).toLocaleDateString(locale || 'fr-FR')}</InfoValue>
              </InfoItem>
            </InfoGrid>
          </Section>

          <Section>
            <SectionTitle>🚚 {t('modal.quoteDetail.transportInfo')}</SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>{t('modal.quoteDetail.fields.transportType')}</InfoLabel>
                <InfoValue>{quote.transport_type}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>{t('modal.quoteDetail.fields.origin')}</InfoLabel>
                <InfoValue>{quote.origin}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>{t('modal.quoteDetail.fields.destination')}</InfoLabel>
                <InfoValue>{quote.destination}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>{t('modal.quoteDetail.fields.distance')}</InfoLabel>
                <InfoValue>{quote.distance_km || 0} km</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>{t('modal.quoteDetail.fields.quantity')}</InfoLabel>
                <InfoValue>{quote.quantity || 0} {quote.unit || 'tonne'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>{t('modal.quoteDetail.fields.desiredDate')}</InfoLabel>
                <InfoValue>
                  {quote.desired_date
                    ? new Date(quote.desired_date).toLocaleDateString(locale || 'fr-FR')
                    : t('modal.quoteDetail.notSpecified')}
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>{t('modal.quoteDetail.fields.roundTrip')}</InfoLabel>
                <InfoValue>{quote.is_round_trip ? t('modal.quoteDetail.yes') : t('modal.quoteDetail.no')}</InfoValue>
              </InfoItem>
            </InfoGrid>
            {quote.comments && (
              <InfoItem style={{ marginTop: '12px' }}>
                <InfoLabel>{t('modal.quoteDetail.fields.comments')}</InfoLabel>
                <InfoValue>{quote.comments}</InfoValue>
              </InfoItem>
            )}
          </Section>

          <Section>
            <SectionTitle>⚙️ {t('modal.quoteDetail.pricingTitle')}</SectionTitle>
            <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'flex-end' }}>
              <Button $variant="secondary" onClick={handleRecalculate} disabled={loading} style={{ fontSize: '13px', padding: '8px 16px' }}>
                🔄 {t('modal.quoteDetail.resetRates')}
              </Button>
            </div>
            <FormGrid>
              <FormField>
                <Label>{t('modal.quoteDetail.fields.distanceKm')}</Label>
                <Input
                  type="number"
                  step="0.1"
                  name="distance_km"
                  value={editedQuote.distance_km}
                  onChange={handleChange}
                />
              </FormField>

              <FormField>
                <Label>{t('modal.quoteDetail.fields.quantityUnit', { unit: quote.unit || 'tonne' })}</Label>
                <Input
                  type="number"
                  step="0.1"
                  name="quantity"
                  value={editedQuote.quantity}
                  onChange={handleChange}
                />
              </FormField>

              <FormField>
                <Label>{t('modal.quoteDetail.fields.transportType')}</Label>
                <Select name="transport_type" value={editedQuote.transport_type} onChange={handleTypeChange}>
                  <option value="">{t('modal.quoteDetail.selectType')}</option>
                  <option value="Camion 10T">Camion 10T</option>
                  <option value="Camion 15T">Camion 15T</option>
                  <option value="Camion 20T">Camion 20T</option>
                  <option value="Camion 25T">Camion 25T</option>
                  <option value="Semi-remorque">Semi-remorque</option>
                  <option value="Camion frigorifique">Camion frigorifique</option>
                  <option value="Citerne">Citerne</option>
                </Select>
              </FormField>

              <FormField>
                <Label>{t('modal.quoteDetail.fields.rateKm')}</Label>
                <Input
                  type="number"
                  step="0.01"
                  name="rate_per_km"
                  value={editedQuote.rate_per_km}
                  onChange={handleChange}
                />
              </FormField>

              <FormField>
                <Label>{t('modal.quoteDetail.fields.rateTonne')}</Label>
                <Input
                  type="number"
                  step="0.01"
                  name="rate_per_tonne"
                  value={editedQuote.rate_per_tonne}
                  onChange={handleChange}
                />
              </FormField>

              <FormField>
                <Label>{t('modal.quoteDetail.fields.fixedFees')}</Label>
                <Input
                  type="number"
                  step="0.01"
                  name="fixed_fees"
                  value={editedQuote.fixed_fees}
                  onChange={handleChange}
                />
              </FormField>

              <FormField>
                <Label>{t('modal.quoteDetail.fields.discount')}</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  name="discount"
                  value={editedQuote.discount}
                  onChange={handleChange}
                />
              </FormField>

              <FormField>
                <Label>{t('modal.quoteDetail.fields.vat')}</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  name="vat"
                  value={editedQuote.vat}
                  onChange={handleChange}
                />
              </FormField>
            </FormGrid>

            <PriceBox>
              <PriceLabel>{t('modal.quoteDetail.price.label')}</PriceLabel>
              <PriceValue>{prices.finalPrice} MAD</PriceValue>
              <PriceBreakdown>
                <BreakdownRow>
                  <span>
                    {t('modal.quoteDetail.price.distance', {
                      distance: quote.is_round_trip ? (quote.distance_km * 2).toFixed(2) : quote.distance_km,
                      rate: editedQuote.rate_per_km
                    })}
                  </span>
                  <span>{prices.distanceCost} MAD</span>
                </BreakdownRow>
                <BreakdownRow>
                  <span>
                    {t('modal.quoteDetail.price.quantity', {
                      quantity: quote.quantity || 0,
                      rate: editedQuote.rate_per_tonne
                    })}
                  </span>
                  <span>{prices.quantityCost} MAD</span>
                </BreakdownRow>
                <BreakdownRow>
                  <span>{t('modal.quoteDetail.price.fixed')}</span>
                  <span>{prices.fixedFees} MAD</span>
                </BreakdownRow>
                <BreakdownRow>
                  <span>{t('modal.quoteDetail.price.subtotal')}</span>
                  <span>{prices.subtotal} MAD</span>
                </BreakdownRow>
                <BreakdownRow>
                  <span>{t('modal.quoteDetail.price.discount', { discount: editedQuote.discount })}</span>
                  <span>-{prices.discountAmount} MAD</span>
                </BreakdownRow>
                <BreakdownRow>
                  <span>{t('modal.quoteDetail.price.afterDiscount')}</span>
                  <span>{prices.priceAfterDiscount} MAD</span>
                </BreakdownRow>
                <BreakdownRow>
                  <span>{t('modal.quoteDetail.price.vat', { vat: editedQuote.vat })}</span>
                  <span>{prices.vatAmount} MAD</span>
                </BreakdownRow>
                <BreakdownRow>
                  <span>{t('modal.quoteDetail.price.total')}</span>
                  <span>{prices.finalPrice} MAD</span>
                </BreakdownRow>
              </PriceBreakdown>
            </PriceBox>
          </Section>

          <ActionButtons>
            <Button $variant="secondary" onClick={handleUpdate} disabled={loading || quote.status !== 'pending'}>
              💾 {t('modal.quoteDetail.buttons.save')}
            </Button>
            
            {quote.status === 'pending' && (
              <>
                <Button $variant="danger" onClick={handleReject} disabled={loading}>
                  ❌ {t('modal.quoteDetail.buttons.reject')}
                </Button>
                <Button $variant="primary" onClick={handleValidate} disabled={loading}>
                  ✅ {t('modal.quoteDetail.buttons.validate')}
                </Button>
              </>
            )}

            {quote.status === 'validated' && (
              <div style={{ color: '#38a169', fontWeight: 600 }}>
                ✅ {t('modal.quoteDetail.badges.validated', { date: new Date(quote.processed_at).toLocaleDateString(locale || 'fr-FR') })}
              </div>
            )}

            {quote.status === 'rejected' && (
              <div style={{ color: '#e53e3e', fontWeight: 600 }}>
                ❌ {t('modal.quoteDetail.badges.rejected', { date: new Date(quote.processed_at).toLocaleDateString(locale || 'fr-FR') })}
              </div>
            )}
          </ActionButtons>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default QuoteDetailModal;
